<script>
    // VAT-ID validation modal — fires after a billing save with a USt-IdNr.
    // when the oxid-proxy `validateVatId` endpoint reports that not every
    // VIES cross-check matched (taxInfo !== -1 and at least one check failed).
    //
    // The backend never blocks the order on a VAT mismatch (soft-fail), so this
    // modal is the single point where the user decides what to do. It mirrors
    // the legacy checklist: one ✅/❌ row per check (VAT validity, company name,
    // street, zip, city). Buttons:
    //
    //   - "Adresse korrigieren" → close + caller switches into editBilling so
    //                             the user can fix the mismatching fields.
    //   - "Trotzdem fortfahren" → record the decision client-side and continue
    //                             the normal flow (status stays informational
    //                             and is still shown on the Order step).
    //
    // Dismiss (ESC / backdrop) is a no-op continue-blocker: the modal just
    // closes and the continue gate re-opens it on the next "Weiter" click, so
    // an unresolved mismatch can't silently slip past.

    import {t} from "$utils/lang.js";

    let {
        open = $bindable(false),
        // Array<{ label: string, ok: boolean, message?: string }> | null.
        // Mirrors userStore.vatValidationResult.checks.
        checks = null,
        // Caller hook. action ∈ 'edit' | 'accept' | 'dismiss'.
        onResolve = (_action) => {},
    } = $props();

    let dialog;

    // Sync `open` ↔ <dialog>.
    $effect(() => {
        if (!dialog) return;
        if (open && !dialog.open) dialog.showModal();
        else if (!open && dialog.open) dialog.close();
    });

    const rows = $derived(Array.isArray(checks) ? checks : []);

    function close(action) {
        open = false;
        onResolve(action);
    }

    // ESC / backdrop dismiss → no decision recorded. The caller's gate keeps
    // the continue blocked, so the user is re-prompted on the next attempt.
    function handleClose() {
        if (open) {
            open = false;
            onResolve('dismiss');
        }
    }
    function handleBackdropClick(e) {
        if (e.target === dialog) handleClose();
    }
</script>

<dialog
    bind:this={dialog}
    onclose={handleClose}
    onclick={handleBackdropClick}
    data-checkout-modal="vat-validation"
    class="m-auto rounded-lg p-0 shadow-2xl backdrop:bg-black/50 backdrop:backdrop-blur-sm w-[480px] max-w-[95vw]"
>
    <div class="bg-white p-5 flex flex-col gap-4">
        <h3 class="text-base font-bold text-cyprus" data-checkout-modal-heading>
            {t('USt-IdNr.-Prüfung')}
        </h3>

        <p class="text-sm text-gray-700">
            {t('Bei der Prüfung deiner USt-IdNr. stimmen nicht alle Angaben mit dem amtlichen Register überein:')}
        </p>

        <ul class="flex flex-col gap-1.5 text-sm" data-checkout-vat-checks>
            {#each rows as check}
                <li
                    class="flex items-start gap-2"
                    data-checkout-vat-check
                    data-checkout-vat-ok={check.ok ? 'true' : 'false'}
                >
                    <span aria-hidden="true">{check.ok ? '✅' : '❌'}</span>
                    <span class="{check.ok ? 'text-gray-700' : 'text-amber-800 font-medium'}">
                        {check.label}
                        {#if !check.ok && check.message}
                            <span class="block text-xs font-normal text-gray-500">{check.message}</span>
                        {/if}
                    </span>
                </li>
            {/each}
        </ul>

        <p class="text-sm text-gray-700">
            {t('Du kannst deine Adresse korrigieren oder mit deinen Angaben fortfahren.')}
        </p>

        <div class="flex flex-col gap-2 pt-2 sm:flex-row sm:flex-wrap sm:justify-end">
            <button
                type="button"
                onclick={() => close('edit')}
                data-checkout-action="vat-edit-address"
                class="px-4 py-2 rounded-md font-semibold text-sm border border-grey hover:bg-gray-50 transition-colors cursor-pointer"
            >
                {t('Adresse korrigieren')}
            </button>
            <button
                type="button"
                onclick={() => close('accept')}
                data-checkout-action="vat-accept"
                class="px-4 py-2 rounded-md font-semibold text-sm bg-cyprus hover:bg-[#003055] text-white transition-colors cursor-pointer"
            >
                {t('Trotzdem fortfahren')}
            </button>
        </div>
    </div>
</dialog>
